import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SECTORS } from '@krit/core';
import { getSettings, patchSettings } from '../lib/api.js';
import { Card, PageHeader } from '../components/ui.js';

type SectorMap = Record<string, { pe: number }>;

export function Peers() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['settings'], queryFn: getSettings, retry: false });
  const saved = (data?.settings?.sectors ?? SECTORS) as SectorMap;

  const [edited, setEdited] = useState<SectorMap | null>(null);
  const sectors = edited ?? saved;
  const dirty = edited !== null && JSON.stringify(edited) !== JSON.stringify(saved);

  const save = useMutation({
    mutationFn: () => patchSettings({ sectors }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); qc.invalidateQueries({ queryKey: ['deals'] }); setEdited(null); },
  });

  const entries = Object.entries(sectors);

  return (
    <div className="max-w-[720px]">
      <PageHeader eyebrow="Tools" title="Peer Multiples" desc="Median listed-peer P/E per sector — anchors valuation for deals without an explicit peer P/E."
        actions={<button disabled={!dirty || save.isPending} onClick={() => save.mutate()} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save'}</button>} />

      <Card title="Sector median P/E" sub={`${entries.length} sectors`} bodyClass="p-0">
        <div className="divide-y divide-line-2">
          {entries.map(([name, v]) => (
            <div key={name} className="flex items-center justify-between px-5 py-2.5">
              <span className="text-[13px] font-semibold text-navy">{name}</span>
              <div className="flex items-center gap-2">
                <input type="number" step="0.1" value={v.pe} onChange={(e) => setEdited({ ...sectors, [name]: { pe: Number(e.target.value) } })} className="w-24 px-2.5 py-1.5 border border-line rounded-lg text-right mono text-[13px]" />
                <span className="text-[11px] text-muted-2 w-3">×</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
      {dirty && <p className="text-[11px] text-amber mt-2">Unsaved changes.</p>}
    </div>
  );
}
